import cn from "classnames"
import { useMemo, useState, useTransition } from "react"

import { Button, Flex, Link, Modal, Text } from "@/atoms"
import { getImportTree } from "@/domain/package"
import {
  getImageNameWithRegistry,
  isOnPremSourceControl
} from "@/domain/resource"
import { useLinkDrawerTooltip } from "@/hooks/useLinkDrawerTooltip"
import { useLogger } from "@/hooks/useLogger"
import { useFeatureFlags } from "@/hooks/useUser"
import {
  ApartmentOutlined,
  ApiOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined
} from "@/icons"
import { ExpandIconTable } from "@/molecules/ExpandIconTable"
import { Heading } from "@/molecules/Heading"
import { InformationAlert } from "@/molecules/InformationAlert"
import { Item, List } from "@/molecules/List"
import { SourceItem } from "@/organisms/Drawers/components/SourceList"
import { findingsToSources } from "@/organisms/Drawers/components/SourceList/utils"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { TabTemplate } from "@/templates/TabTemplate"
import { IntroducedThroughCard } from "../IntroducedThroughCard"
import { getColumnsTable } from "./getColumnsTable"
import { ImageIntroduceThrough } from "./ImageIntroduceThrough"

import styles from "./IntroduceThroughModal.module.scss"

import type {
  ApplicativeImageFinding,
  BaseImageFinding,
  CodeRepoFinding,
  Resource,
  ThirdPartyFinding,
  VmFinding
} from "@/domain/issue"

import type { ImportItem } from "@/domain/package/package.types"
import type { TFunction } from "@/hooks/useTranslation"

export type IntroduceThroughModalProps = {
  codeRepoFindings: CodeRepoFinding[]
  imageFindings: ApplicativeImageFinding[]
  baseImageFindings: BaseImageFinding[]
  thirdPartyFindings?: ThirdPartyFinding[]
  vmFindings: VmFinding[]
  buttonText: string
  title: string
  description: string
  icon?: React.ReactNode
  drawer?: boolean
  defaultExpandedRowKeys?: string[]
  isLoading?: boolean
  SearchComponent?: React.ReactNode
  showResolvedFindingsAlert?: boolean
  renderResourceAside?: (resource: Resource) => React.ReactNode
  renderSourceAside?: (source: SourceItem) => React.ReactNode
}

type DataSourceItem = {
  key: string
  resource: Resource & {
    findingsCount?: number
    resolvedFindingsCount?: number
  }
  introducedThrough: any
  expandable: boolean
  sources: SourceItem[]
  importPaths?: ImportItem[][]
  importTree?: ReturnType<typeof getImportTree<ImportItem>>
  packageLocations?: string[]
}

function generateDataSource(
  codeRepoFindings: CodeRepoFinding[],
  baseImageFindings: BaseImageFinding[],
  thirdPartyFindings: ThirdPartyFinding[],
  imageFindings: ApplicativeImageFinding[],
  vmFindings: VmFinding[]
): DataSourceItem[] {
  const sources = findingsToSources(codeRepoFindings)
  const repo = codeRepoFindings?.[0]
  const imageSources = [
    ...baseImageFindings,
    ...thirdPartyFindings,
    ...imageFindings
  ].map((value) => ({
    key: `${value.resource.id}`,
    expandable: true,
    resource: {
      popoverName: getImageNameWithRegistry(
        value.resource,
        value.resource.name
      ),
      isResolved: value.isResolved,
      resolutionData: value.resolutionData,
      ...value.resource
    },
    introducedThrough: value.introducedThrough,
    importPaths: value.importPaths,
    importTree: getImportTree(value.importPaths),
    sources: []
  }))
  const vmSources = (vmFindings || []).map((value, index) => {
    const {
      subjectType,
      vmInstanceId,
      autoScaleGroup,
      packageLocations = []
    } = value?.introducedThrough?.[0] || {}

    return {
      key: `${vmInstanceId || index}`,
      expandable: !!sources.length,
      resource: {
        subjectType,
        ...value?.resource,
        name: vmInstanceId,
        subTitle: autoScaleGroup
      },
      introducedThrough: value?.introducedThrough,
      sources: [],
      packageLocations
    }
  })
  if (repo) {
    // adding the repo source first
    // it would be the same source for all the repos
    const findingsCount = codeRepoFindings?.length || 0
    const resolvedFindingsCount =
      codeRepoFindings?.filter((f) => f.isResolved).length || 0

    return [
      {
        key: repo.resource.id,
        expandable: true,
        resource: {
          findingsCount,
          resolvedFindingsCount,
          isResolved:
            findingsCount > 0 && findingsCount === resolvedFindingsCount,
          resolutionData: repo.resolutionData,
          ...repo.resource
        },
        introducedThrough: [],
        sources
      },
      ...imageSources,
      ...vmSources
    ]
  }
  return [...imageSources, ...vmSources]
}

export const IntroduceThroughModal: React.FC<
  IntroduceThroughModalProps & { t: TFunction; tKey: string }
> = ({
  codeRepoFindings,
  baseImageFindings,
  thirdPartyFindings = [],
  imageFindings,
  vmFindings,
  buttonText,
  title,
  description,
  icon,
  t,
  tKey,
  drawer,
  isLoading,
  SearchComponent,
  defaultExpandedRowKeys = [],
  showResolvedFindingsAlert = false,
  renderResourceAside,
  renderSourceAside
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { logger } = useLogger()

  const showModal = () => {
    setIsModalOpen(true)
  }
  const closeModal = () => {
    setIsModalOpen(false)
  }

  const drawerProps = useDrawer("")
  const { open, openDrawer } = useLinkDrawerTooltip(drawerProps)

  const [, setTransition] = useTransition()
  const columns = getColumnsTable({
    t,
    openPopover: open,
    renderAside: renderResourceAside,
    onRowClick: ({ key }, drawerId) => {
      try {
        openDrawer(drawerId, key)

        document
          .querySelectorAll(".popover-resource-cell")
          .forEach((element) => {
            if (!element.classList.contains("ant-popover-hidden")) {
              ;(element as HTMLElement).style.zIndex = "-1"
              element.classList.add("ant-popover-hidden")
            }
          })

        setTransition(() => {
          closeModal()
        })
      } catch (err) {
        logger.error(err as Error)
      }
    }
  })

  const dataSource = useMemo(
    () =>
      generateDataSource(
        codeRepoFindings,
        baseImageFindings,
        thirdPartyFindings,
        imageFindings,
        vmFindings
      ),
    [
      codeRepoFindings,
      baseImageFindings,
      thirdPartyFindings,
      imageFindings,
      vmFindings
    ]
  )

  const Component = drawer ? "div" : Modal
  const kortexInstalledFF = useFeatureFlags("kortexInstalled")
  const scmInstalledFF = useFeatureFlags("scmInstalled")

  return (
    <>
      {!drawer && (
        <div>
          <Button
            type="link"
            onClick={() => showModal()}
            size="small"
            className={styles.button}
          >
            {icon && icon}
            {buttonText}
          </Button>
        </div>
      )}
      <Component
        closable={drawer ? undefined : true}
        open={isModalOpen}
        width={1200}
        footer={null}
        onCancel={closeModal}
        className={styles.modal}
      >
        <div className={styles.container}>
          {title && (
            <div className={cn(styles.title, drawer && styles.drawer)}>
              {!drawer && <ApartmentOutlined />}
              <Heading level={5} title={title} />
            </div>
          )}
          {description && (
            <Text className={styles.description}>{description}</Text>
          )}
          {showResolvedFindingsAlert && (
            <div className={styles.resolvedFindingsWrapper}>
              <InformationAlert
                type="success"
                palette="success"
                icon={<CheckCircleOutlined />}
                message={t(
                  "inventory.codeRepositoriesTab.drawer.resolvedFindingsMessage"
                )}
              />
            </div>
          )}
          <div className={cn(!SearchComponent && styles.tableWrapper)}>
            <TabTemplate
              loading={!!isLoading}
              columns={columns}
              dataSource={dataSource}
              expandable={{
                defaultExpandedRowKeys: defaultExpandedRowKeys,
                expandIcon: ExpandIconTable,
                expandRowByClick: true,
                expandedRowRender: (
                  record: DataSourceItem,
                  _,
                  __,
                  expanded
                ) => {
                  if (!expanded) return null

                  const {
                    resource,
                    introducedThrough,
                    packageLocations,
                    sources,
                    importTree
                  } = record

                  if (resource.type === "Vm") {
                    return (
                      <div className={styles.introducedThroughSourcesList}>
                        <List
                          title="Package Locations"
                          items={packageLocations || []}
                          limit={2}
                          render={(path: string, index) => (
                            <Item
                              key={index}
                              title={<Text copyable>{path}</Text>}
                            />
                          )}
                        />
                      </div>
                    )
                  }

                  const hasImageIntroduceThrough =
                    resource.type === "Image" && introducedThrough.length > 0
                  return (
                    <div>
                      {hasImageIntroduceThrough && (
                        <ImageIntroduceThrough
                          introducedThrough={introducedThrough}
                          t={t}
                          tKey={tKey}
                          importTree={importTree}
                        />
                      )}
                      {resource.type === "CodeRepository" && (
                        <>
                          <Flex
                            vertical
                            gap={24}
                            className={styles.introducedThroughCardWrapper}
                          >
                            {sources.map((source, index) => (
                              <IntroducedThroughCard
                                key={`${source.name}_${index}`}
                                path={source.name}
                                sourceControlLink={source.sourceControlLink}
                                gitMetadata={source.gitMetadata}
                                isOnPrem={isOnPremSourceControl(
                                  record.resource
                                )}
                                importTree={getImportTree(
                                  source.introducedThrough
                                )}
                                isResolved={source.isResolved}
                                resolutionData={source.resolutionData}
                                sourceAside={renderSourceAside?.(source)}
                                isResolvedInCodeRepoOnly={
                                  source.isResolvedInCodeRepoOnly
                                }
                              />
                            ))}
                          </Flex>
                        </>
                      )}
                    </div>
                  )
                }
              }}
              classes={{ footer: styles.footer }}
              SearchComponent={SearchComponent}
            />
            {!isLoading && !kortexInstalledFF && (
              <Link to="/deployments">
                <Button
                  className={styles.deployButton}
                  type="link"
                  icon={<CloudUploadOutlined />}
                >
                  Deploy Kodem runtime sensor to view related images
                </Button>
              </Link>
            )}
            {!isLoading && !scmInstalledFF && (
              <Link to="/integrations">
                <Button
                  className={styles.deployButton}
                  type="link"
                  icon={<ApiOutlined />}
                >
                  Connect Kodem With SCM System To View Related Code
                  Repositories And Remediation Suggestions
                </Button>
              </Link>
            )}
          </div>
        </div>
      </Component>
    </>
  )
}
export default IntroduceThroughModal
